import FormField from 'components/FormField';
import { FV } from './share';
import { DisabledProps } from '../TaskForm/share';
import {
  scenarioFirstRoundMinTimeMap,
  scenarioTriggerActionI18nMap,
  scenarioTypeI18nLabelMap,
} from '../config';
import { InlineItem, useGetTimes } from '../RoundForm';
import useMixingTokoText from 'hooks/business/useMixingTokoText';
import TransComponent from 'components/TransComponent';
import { InputNumber, Select } from 'antd';
import { useFormikContext } from 'formik';
import { T, useTranslate } from '@tolgee/react';

const unitTimeMinMap = {
  1: 2,
  2: 1,
};
const unitTimeMaxMap = {
  1: 1440,
  2: 24,
};

function RoundConfigField({
  scenarioType,
  disabled,
}: DisabledProps & {
  scenarioType: Marketing.ScenarioType | 'INVITE_FOLLOWER';
}) {
  const firstRoundMinTime = scenarioFirstRoundMinTimeMap[scenarioType];

  const firstRoundMaxTime = scenarioType === 'INVITE_FOLLOWER' ? 360 : 30;

  const { getTimes } = useGetTimes();

  const { t } = useTranslate();

  const {
    orderReminderTitle: { text: orderReminderTitle },
  } = useMixingTokoText();
  const scenarioLabel =
    scenarioType === 'ORDER_REMINDER'
      ? orderReminderTitle
      : (t(scenarioTypeI18nLabelMap[scenarioType]) as string);

  const {
    values: { scenarioConfig: { praiseTaskConfig: { triggerPoint } = {} } = {}, round },
  } = useFormikContext<FV>();

  return (
    <FormField<FV['roundConfig']>
      name="roundConfig"
      required
      label={t('marketing.执行时间')}
      validate={(value) => {
        const { executeTime, executeTimeUnit } = value || {};

        if ((!executeTime && executeTime !== 0) || !executeTimeUnit) {
          return t('marketing.请输入执行时间');
        }

        if (round === 1) {
          if (executeTime > firstRoundMaxTime || executeTime < firstRoundMinTime) {
            return t('marketing.执行时间不得超出范围x-y timeUnit', {
              x: firstRoundMinTime,
              y: firstRoundMaxTime,
              timeUnit: t('marketing.分钟'),
            });
          }
        } else {
          const min = unitTimeMinMap[executeTimeUnit];
          const max = unitTimeMaxMap[executeTimeUnit];

          const unitLabelMap = {
            '1': t('marketing.分钟'),
            '2': t('marketing.小时'),
          };

          if (executeTime > max || executeTime < min) {
            return t('marketing.执行时间不得超出范围x-y timeUnit', {
              x: min,
              y: max,
              timeUnit: unitLabelMap[executeTimeUnit],
            });
          }
        }
      }}
    >
      {({ field: { value, onChange, onBlur } }) => {
        const { executeTime, executeTimeUnit } = value || {};

        const time = (
          <TransComponent
            content={
              <InlineItem>
                <InputNumber
                  value={executeTime}
                  onChange={(executeTime) =>
                    onChange({
                      executeTimeUnit: 1,
                      ...value,
                      executeTime: executeTime ?? 1,
                    })
                  }
                  onBlur={onBlur}
                  disabled={disabled}
                  min={
                    round === 1 || !executeTimeUnit
                      ? firstRoundMinTime
                      : unitTimeMinMap[executeTimeUnit]
                  }
                  max={
                    round === 1 || !executeTimeUnit
                      ? firstRoundMaxTime
                      : unitTimeMaxMap[executeTimeUnit]
                  }
                  precision={0}
                />
              </InlineItem>
            }
          />
        );

        const unit =
          round === 1 ? (
            <TransComponent content={<>{t('marketing.分钟')}</>} />
          ) : (
            <TransComponent
              content={
                <InlineItem>
                  <Select
                    dropdownMatchSelectWidth={false}
                    disabled={disabled}
                    value={executeTimeUnit}
                    onChange={(executeTimeUnit) => {
                      onChange({
                        executeTime: 1,
                        ...value,
                        executeTimeUnit,
                      });
                    }}
                    options={[
                      {
                        value: 1,
                        label: t('marketing.分钟'),
                      },
                      {
                        value: 2,
                        label: t('marketing.小时'),
                      },
                    ]}
                    allowClear={false}
                  />
                </InlineItem>
              }
            />
          );

        const triggerAction = t(
          scenarioType === 'PRAISE_REMINDER'
            ? scenarioTriggerActionI18nMap.PRAISE_REMINDER[triggerPoint || 'CONFIRMED']
            : scenarioTriggerActionI18nMap[scenarioType],
        );

        if (round === 1) {
          return (
            <T
              keyName="marketing.action x timeUnit后"
              params={{ action: triggerAction, x: time, timeUnit: unit }}
            />
          );
        }

        if (scenarioType === 'PAYMENT_REMINDER') {
          return (
            <T
              keyName="marketing.第x次action y timeUnit后"
              params={{
                x: getTimes(round - 1),
                action: scenarioLabel,
                y: time,
                timeUnit: unit,
              }}
            />
          );
        }

        return (
          <T
            keyName="marketing.第x次action1或最后一次action2 y timeUnit后"
            params={{
              x: getTimes(round - 1),
              action1: scenarioLabel,
              action2: triggerAction,
              y: time,
              timeUnit: unit,
            }}
          />
        );
      }}
    </FormField>
  );
}

export default RoundConfigField;
